import { useState } from 'react';

import {
  Dialog,
  DialogContent as DialogBody,
  DialogActions as DialogFooter,
  DialogTitle as DialogHeader,
  IconButton,
  Theme,
  WithStyles,
  createStyles,
  styled,
  withStyles,
} from '@material-ui/core';
import { TabPanelProps } from '@material-ui/lab';

import {
  RiExternalLinkLine as OpenInNewIcon,
  RiCloseLine,
} from '@remixicon/react';
import classNames from 'classnames';

import { Copy } from '@spotify/confidence-core-react';

import { JsonDiff, JsonSideBySide } from '../JsonDiff';
import ActivityItemTabPanel from './ActivityItemTabPanel';

const styles = (theme: Theme) =>
  createStyles({
    root: {},
    json: {
      overflow: 'auto',
    },
    dialog: {
      backgroundColor: theme.palette.action.hover,
    },
  });

export interface JsonPanelProps
  extends Omit<TabPanelProps, 'value' | 'classes'>,
    WithStyles<typeof styles> {
  oldValue: object | undefined;
  newValue: object | undefined;
}

const OpenDialogButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
}));

const ActivityItemJsonPanel = ({
  classes,
  oldValue,
  newValue,
  className,
  ...rest
}: JsonPanelProps) => {
  const [open, setOpen] = useState(false);

  return (
    <ActivityItemTabPanel
      value="json"
      {...rest}
      className={classNames(className, classes.json)}
    >
      <OpenDialogButton size="small" onClick={() => setOpen(true)}>
        <OpenInNewIcon size={20} />
      </OpenDialogButton>
      <JsonDiff
        className={classes.json}
        oldValue={oldValue}
        newValue={newValue}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        PaperProps={{
          style: { maxWidth: 'clamp(400px, 90vw, 1200px)', maxHeight: '80vh' },
        }}
      >
        <DialogHeader>
          Change
          <IconButton
            aria-label="Close"
            onClick={() => setOpen(false)}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <RiCloseLine size={16} />
          </IconButton>
        </DialogHeader>
        <DialogBody>
          <JsonSideBySide oldValue={oldValue} newValue={newValue} />
        </DialogBody>
        <DialogFooter>
          {oldValue && (
            <Copy
              value={JSON.stringify(oldValue, null, 2)}
              label="Copy old value to clipboard"
            />
          )}
          {newValue && (
            <Copy
              value={JSON.stringify(newValue, null, 2)}
              label="Copy new value to clipboard"
            />
          )}
        </DialogFooter>
      </Dialog>
    </ActivityItemTabPanel>
  );
};

export default withStyles(styles)(ActivityItemJsonPanel);
